import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useMemo } from 'react';
import { BoilerAdapter, BoilerCascade, BoilerGroup, HeatingCircuit, } from 'z3k-config';
import { findAndChangeLinks } from 'z3k-parser/lib/find-links';
import { unpack_validation } from 'z3k-parser/validation/types';
import { Alert } from '@mui/material';
import { useBox } from 'shared/hooks/utils';
import { Z3KSettings, Z3KSettingsEditor, } from 'console/device-settings/z3k/provider/types';
import { DragDropDrawer } from '../utils/drag-drop-drawer/component';
import { RouteNaming, Z3kSecondRoutes } from 'z3k-settings/routes/type';
import { Z3kDragDropContainer } from '../utils/drag-drop-drawer/container';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { SelectOutsideSensor } from 'z3k-settings/components/heating-circuits/select-outside';
export function HeatingCircuitsList() {
    const { replace_config, config } = useContext(Z3KSettingsEditor);
    const configBox = useBox(config);
    const { device_spec, service_view, addIdToBlacklist } = useContext(Z3KSettings);
    const [cascade_changed, , setCascadeChanged] = useBooleanState(false);
    const onChangeCascade = useCallback((new_list) => {
        setCascadeChanged();
        replace_config({ ...configBox.current, boiler_cascades: new_list });
    }, [setCascadeChanged, replace_config]);
    const onChange = useCallback((new_list) => {
        replace_config({
            ...configBox.current,
            heating_circuits: new_list.map((hc) => HeatingCircuit.isBoiler(hc) &&
                hc.control_devices.length == 1 &&
                BoilerAdapter.get.byId(configBox.current, hc.control_devices[0]) !=
                    null
                ? {
                    ...hc,
                    external_thermostat: null,
                    water_temp_sensor: hc.water_temp_sensor ??
                        hc.control_devices[0],
                }
                : hc),
        });
    }, [replace_config]);
    const updateCascadeFlag = useCallback((config, boiler_group_list) => {
        const boilerIdList = boiler_group_list.flatMap(({ boiler_list }) => boiler_list);
        return HeatingCircuit.get.modify(config, () => true, (hc) => HeatingCircuit.setCascadeFlag(hc, boilerIdList.includes(hc.id)));
    }, []);
    const onChangeGroupOption = useCallback((new_list) => {
        replace_config({
            ...updateCascadeFlag(configBox.current, new_list),
            boiler_groups: new_list,
        });
    }, [replace_config, updateCascadeFlag]);
    const onDeleteBoilerGroup = useCallback((group, new_list) => {
        addIdToBlacklist(group.id);
        replace_config(updateCascadeFlag({
            ...findAndChangeLinks(configBox.current, group.id),
            boiler_groups: new_list,
        }, new_list));
    }, [replace_config, updateCascadeFlag]);
    const boiler_groups = useMemo(() => BoilerGroup.get.all(config), [config]);
    const filter_hc = useCallback((hc) => service_view || !HeatingCircuit.isHidden(hc), [service_view]);
    const childrenItem = (selected_item_id) => {
        return (_jsxs(_Fragment, { children: [_jsx(DragDropDrawer, { z3k_name: "heating_circuits", name: RouteNaming[Z3kSecondRoutes.heating_circuit], help_id: "heating_circuits", enableDnd: true, item_url: Z3kSecondRoutes.heating_circuit, hideAddButton: !service_view, hideTrashButton: !service_view, selected_item_id: selected_item_id, objectFilter: filter_hc, onChange: onChange }), service_view && (_jsxs(_Fragment, { children: [(boiler_groups.length > 0 ||
                            (device_spec.heating.has_heating &&
                                device_spec.heating.has_extended_heating
                                    .show_boiler_groups)) && (_jsxs(_Fragment, { children: [_jsx(DragDropDrawer, { z3k_name: "boiler_groups", name: RouteNaming[Z3kSecondRoutes.boiler_groups], help_id: "boiler_groups", item_url: Z3kSecondRoutes.boiler_groups, hideAddButton: true, onChange: onChangeGroupOption, onDelete: onDeleteBoilerGroup, selected_item_id: selected_item_id }), _jsx(Alert, { severity: "error", children: "\u0427\u0442\u043E\u0431\u044B \u0441\u043E\u0437\u0434\u0430\u0442\u044C \u043A\u0430\u0441\u043A\u0430\u0434 \u043A\u043E\u0442\u043B\u043E\u0432 \u0443\u0434\u0430\u043B\u0438\u0442\u0435 \u0432\u0441\u0435 \u0433\u0440\u0443\u043F\u043F\u044B \u043A\u043E\u0442\u043B\u043E\u0432. \u0410\u043A\u0442\u0443\u0430\u043B\u044C\u043D\u043E \u0442\u043E\u043B\u044C\u043A\u043E \u0434\u043B\u044F \u0432\u0435\u0440\u0441\u0438\u0438 \u043F\u0440\u043E\u0448\u0438\u0432\u043A\u0438 70 \u0438 \u0432\u044B\u0448\u0435" })] })), (boiler_groups.length > 0 ||
                            (device_spec.heating.has_heating &&
                                device_spec.heating.has_extended_heating
                                    .show_boiler_cascades)) && (_jsxs(_Fragment, { children: [cascade_changed && (_jsx(Alert, { severity: "warning", children: "\u041F\u043E\u0441\u043B\u0435 \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u044F \u043D\u0435\u043E\u0431\u0445\u043E\u0434\u0438\u043C\u0430 \u043F\u0435\u0440\u0435\u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u043F\u0440\u0438\u0431\u043E\u0440\u0430" })), _jsx(DragDropDrawer, { z3k_name: "boiler_cascades", name: RouteNaming[Z3kSecondRoutes.boiler_cascades], item_url: Z3kSecondRoutes.boiler_cascades, help_id: "boiler_cascades", hideAddButton: BoilerCascade.get.all(configBox.current)
                                        .length > 0, onChange: onChangeCascade, selected_item_id: selected_item_id })] })), _jsx(SelectOutsideSensor, {})] }))] }));
    };
    return _jsx(Z3kDragDropContainer, { childrenItem: childrenItem });
}
const fields = [
    'heating_circuits',
    'boiler_groups',
    'boiler_cascades',
];
HeatingCircuitsList.isError = (validation_result) => fields.reduce((prev, current) => prev || !!unpack_validation(validation_result, current), false);
HeatingCircuitsList.isWarning = (validation_result, service_view) => {
    if (!service_view) {
        return false;
    }
    return fields.reduce((prev, current) => prev || !!unpack_validation(validation_result, current), false);
};
HeatingCircuitsList.isShown = (is_service_view, config, spec, is_staff, is_climatic) => spec.heating.has_heating && !is_climatic;
